import { Button } from '@avanta/shared/components/ui/Button';
import { Dialog } from '@avanta/shared/components/ui/Dialog';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEnterpriseId } from 'stores/user';
import { useGetContractsOptions } from '../../hooks/useGetContractsOptions';
import { type AddAdministratorForm, addAdministratorForm } from './model';
import { useCreateAdmin } from './useCreateAdmin';

export const AddAdministrator = (): React.ReactElement => {
  const enterpriseId = useEnterpriseId();
  const { hideDialog } = useDialogContext();
  const { mutate: createAdmin, isPending: isCreatingAdmin } = useCreateAdmin();
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<AddAdministratorForm>({
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      role: '',
      contractIds: [],
    },
    mode: 'onChange',
  });
  const [contractIds] = watch(['contractIds']);
  const optionsMap = useGetContractsOptions(setValue, contractIds);

  const handleOnSubmit = async (data: AddAdministratorForm): Promise<void> => {
    createAdmin({
      enterpriseId,
      name: data.name,
      surname: data.surname,
      email: data.email,
      role: data.role,
      contractIds: data.contractIds,
    });
  };

  return (
    <Dialog title={t('AdministratorsPage.addAdmin.cta')} open className="max-h-131 overflow-y-auto">
      <form className="flex flex-col gap-8" onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(addAdministratorForm).map(([id, input]) => {
            const { component: Component } = input;
            const isControllerComponent = Component !== Input;
            const formId = id as keyof typeof addAdministratorForm;
            const formErrors = errors?.[formId];
            const options = optionsMap[formId as keyof typeof optionsMap] ?? input.options ?? [];
            const rules = {
              required: input.required,
              ...(input.pattern && { pattern: input.pattern }),
              ...(input.validate && { validate: input.validate }),
              ...(input.minLength && { minLength: input.minLength }),
              ...(input.maxLength && { maxLength: input.maxLength }),
            };

            return (
              <div key={id}>
                {isControllerComponent ? (
                  <Component
                    control={control}
                    name={formId}
                    className="w-full"
                    label={t(input?.label)}
                    placeholder={t(input?.placeholder ?? '')}
                    options={options.map(option => ({
                      ...option,
                      description:
                        option.description &&
                        t(option.description, option.ns ? { ns: option.ns } : {}),
                    }))}
                    rules={rules}
                    multiple={input.multiple}
                    searchable={input.searchable}
                    variant={formErrors ? 'invalid' : 'filled'}
                    leftIcon={input.leftIcon}
                  />
                ) : (
                  <Input
                    type={input?.type}
                    className="w-full"
                    label={t(input?.label)}
                    placeholder={t(input?.placeholder ?? '')}
                    {...register(formId, rules)}
                    variant={!!formErrors ? 'invalid' : 'filled'}
                    leftIcon={input.leftIcon}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="destructive_terciary"
            type="button"
            className="w-fit"
            onClick={hideDialog}
            size="lg"
            icon="leading"
          >
            <XIcon className="size-6 text-red-400" strokeWidth={0.75} />
            {t('Common.cancel', { ns: LOCIZE_NS.COMMON })}
          </Button>
          <Button
            type="submit"
            variant="secondary"
            className="min-w-40"
            disabled={!isDirty || !isValid}
            isLoading={isCreatingAdmin}
            size="lg"
          >
            {t('InsuranceAdministrators.addAdmin.cta')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
